// angular
import {Component, OnInit, inject} from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import {TranslateModule} from "@ngx-translate/core";
// material angular
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import {MatCardModule} from "@angular/material/card";
import {MatButtonModule} from "@angular/material/button";
// components
import {InputComponent} from "@app/core/components/input/input.component";
import {TextareaComponent} from "@app/core/components/textarea/textarea.component";
import {SelectComponent} from "@app/core/components/select/select.component";
import {DatePickerComponent} from "@app/core/components/date-picker/date-picker.component";
import {ButtonComponent} from "@app/core/components/button/button.component";
// services
import {WalletService} from "@app/+customer/shared/services/wallet.service";
import {AppToastService} from "@app/shared/services/app-toast.service";
// types
import {PaymentMethodTypeEnum} from "@app/+customer/shared/types/payment-method.type";
// utils
import {generateSelectOptionsFromEnum} from "@app/shared/utils/generateSelectOptionsFromEnum";
import convertDateToISO from "@app/shared/utils/convertDateToISO";
// directives
import {OnlyNumberDirective} from "@app/core/directives/only-number.directive";
import {CommasSeparateNumbersDirective} from "@app/core/directives/commas-separate-numbers.directive";
import {PreventFormSubmitDirective} from "@app/core/directives/form-enter-handler.directive";

@Component({
  selector: "app-payment-dialog",
  templateUrl: "./payment-dialog.component.html",
  styleUrls: ["./payment-dialog.component.scss"],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    MatCardModule,
    InputComponent,
    TextareaComponent,
    SelectComponent,
    MatButtonModule,
    DatePickerComponent,
    MatDialogModule,
    OnlyNumberDirective,
    CommasSeparateNumbersDirective,
    PreventFormSubmitDirective,
    ButtonComponent,
  ],
})
export class PaymentDialogComponent implements OnInit {
  public form: FormGroup;
  public isLoading: boolean = false;

  public paymentMethodTypeEnum = PaymentMethodTypeEnum;
  public selectedPaymentMethod: PaymentMethodTypeEnum =
    this.paymentMethodTypeEnum.CASH;
  public paymentMethodOptionsTR = generateSelectOptionsFromEnum(
    PaymentMethodTypeEnum
  );

  // dependencies
  private _paymentService = inject(WalletService);
  private _fb = inject(FormBuilder);
  private _toastService = inject(AppToastService);
  public dialogRef = inject<MatDialogRef<PaymentDialogComponent>>(MatDialogRef);
  public data = inject<{customerId: string; payment?: any}>(MAT_DIALOG_DATA);

  constructor() {}

  ngOnInit() {
    this._buildForm();
    if (this.data?.payment) {
      this.selectedPaymentMethod = this.data.payment.method;
      if (this.data.payment.method === this.paymentMethodTypeEnum.CHEQUE) {
        this.form.addControl(
          "cheque_due_date",
          new FormControl(
            this.data.payment ? this.data.payment.cheque_due_date : null,
            Validators.required
          )
        );
      }
    }
  }

  private _buildForm() {
    this.form = this._fb.group({
      customer_id: new FormControl(this.data.customerId, Validators.required),
      amount: new FormControl(
        this.data.payment ? this.data.payment.amount : null,
        Validators.required
      ),
      method: new FormControl(
        this.data.payment ? this.data.payment.method : null,
        Validators.required
      ),
      description: new FormControl(
        this.data.payment ? this.data.payment.description : null
      ),
      date: new FormControl(
        this.data.payment ? this.data.payment.date : null,
        Validators.required
      ),
    });
  }

  submit() {
    if (this.form.valid) {
      const params = this.form.value;
      params.date = convertDateToISO(params.date);
      if (this.selectedPaymentMethod === this.paymentMethodTypeEnum.CHEQUE) {
        params.cheque_due_date = convertDateToISO(params.cheque_due_date);
      }
      params.amount = params.amount.replaceAll(",", "");
      params.amount = parseInt(params.amount);

      //  set loading state
      this.isLoading = true;

      // create payment
      if (!this.data?.payment) {
        this._paymentService.addPayment(params).subscribe({
          next: () => {
            this.isLoading = false;
            this._toastService.success(
              "wallet.newPaymentSuccess",
              "toast.success",
              {
                closeButton: false,
                titleClass: "hidden",
              }
            );
            this.cancel();
          },
          error: () => {
            this.isLoading = false;
            this._toastService.error("wallet.newPaymentFail", "toast.error", {
              closeButton: false,
              titleClass: "hidden",
            });
          },
        });
      } else {
        // update payment
        this._paymentService
          .editPayment(this.data.payment.id, params)
          .subscribe({
            next: () => {
              this.isLoading = false;
              this._toastService.success(
                "wallet.updatePaymentSuccess",
                "toast.success",
                {
                  closeButton: false,
                  titleClass: "hidden",
                }
              );
              this.cancel();
            },
            error: () => {
              this.isLoading = false;
              this._toastService.error(
                "wallet.updatePaymentFail",
                "toast.error",
                {
                  closeButton: false,
                  titleClass: "hidden",
                }
              );
            },
          });
      }
    }
  }

  selectionChange(newType: any) {
    this.selectedPaymentMethod = newType;
    if (newType === this.paymentMethodTypeEnum.CHEQUE) {
      this.form.addControl(
        "cheque_due_date",
        new FormControl("", Validators.required)
      );
    }
  }
  cancel() {
    this.dialogRef.close();
  }
}
